import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
    getKycRequestDetails,
    updateKycRequestStatus,
    viewKycDocument,
    downloadKycDocument,
} from '@/api/kyc.ts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Check, FileText, User, X, Shield } from 'lucide-react';
import { format } from 'date-fns';
import { KycRequestUpdateRequest } from '@/models/kyc.ts';
import UploadKycDocumentsModal from '@/components/kyc/UploadKycDocumentsModal';
import DocumentCard from '@/components/shared/DocumentCard';

const KycDetailsPage = () => {
    const { id = '' } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const [rejectionReason, setRejectionReason] = useState('');
    const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
    const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [overrideReason, setOverrideReason] = useState('');
    const [isAdminOverride, setIsAdminOverride] = useState(false);
    const [isUploadDocumentsModalOpen, setIsUploadDocumentsModalOpen] = useState(false);

    const {
        data: kycRequest,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['kycRequestDetails', id],
        queryFn: () => getKycRequestDetails(id),
        enabled: !!id,
    });

    const mutation = useMutation({
        mutationFn: (data: KycRequestUpdateRequest) => updateKycRequestStatus(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kycRequestDetails', id] });
            queryClient.invalidateQueries({ queryKey: ['adminKycRequests'] });
            setIsRejectDialogOpen(false);
            setIsApproveDialogOpen(false);
            setRejectionReason('');
            setAdminNotes('');
            setOverrideReason('');
            setIsAdminOverride(false);
        },
    });

    const handleApprove = () => {
        const updateData: KycRequestUpdateRequest = {
            status: 'approved',
            admin_override: isAdminOverride || kycRequest?.status === 'rejected',
            admin_notes: adminNotes || undefined,
            override_reason: overrideReason || undefined,
        };
        mutation.mutate(updateData);
    };

    const handleReject = () => {
        if (rejectionReason.trim()) {
            const updateData: KycRequestUpdateRequest = {
                status: 'rejected',
                rejection_reason: rejectionReason.trim(),
                admin_override: isAdminOverride || kycRequest?.status !== 'pending',
                admin_notes: adminNotes || undefined,
                override_reason: overrideReason || undefined,
            };
            mutation.mutate(updateData);
        }
    };

    const handleViewDocument = async (document: any) => {
        try {
            await viewKycDocument(id, document.id);
        } catch (error) {
            console.error('Error viewing document:', error);
            enqueueSnackbar('Failed to open document. Please try again.', { variant: 'error' });
        }
    };

    const handleDownloadDocument = async (document: any) => {
        try {
            await downloadKycDocument(id, document.id);
        } catch (error) {
            console.error('Error downloading document:', error);
            enqueueSnackbar('Failed to download document. Please try again.', {
                variant: 'error',
            });
        }
    };

    if (isLoading) {
        return (
            <PageContainer>
                <div className="flex items-center gap-4">
                    <Skeleton className="h-9 w-40 rounded-md" />
                    <div className="space-y-1">
                        <Skeleton className="h-8 w-56" />
                        <Skeleton className="h-4 w-64" />
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Card key={i}>
                                <CardHeader>
                                    <Skeleton className="h-6 w-40" />
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {Array.from({ length: 4 }).map((_, j) => (
                                            <div key={j} className="space-y-2">
                                                <Skeleton className="h-4 w-24" />
                                                <Skeleton className="h-5 w-32" />
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <Skeleton className="h-6 w-32" />
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <Skeleton className="h-9 w-full rounded-md" />
                                <Skeleton className="h-9 w-full rounded-md" />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </PageContainer>
        );
    }

    if (error || !kycRequest) {
        return (
            <PageContainer>
                <PageHeader
                    title="KYC Request Details"
                    backTo="/kyc-requests"
                    backLabel="Back to KYC requests"
                />
                <p className="text-destructive">
                    Error loading KYC request: {error?.message || 'Unknown error'}
                </p>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title="KYC Request Details"
                description="Review and manage KYC verification request"
                backTo="/kyc-requests"
                backLabel="Back to KYC requests"
                actions={
                    <>
                        {/* Upload Documents Button */}
                        <Button
                            variant="outline"
                            onClick={() => setIsUploadDocumentsModalOpen(true)}
                        >
                            <FileText className="h-4 w-4" />
                            Upload Documents
                        </Button>

                        {/* Show approve button for pending and rejected requests */}
                        {(kycRequest.status === 'pending' || kycRequest.status === 'rejected') && (
                            <Dialog
                                open={isApproveDialogOpen}
                                onOpenChange={setIsApproveDialogOpen}
                            >
                                <DialogTrigger asChild>
                                    <Button variant="success">
                                        <Check className="h-4 w-4" />
                                        {kycRequest.status === 'rejected'
                                            ? 'Override & Approve'
                                            : 'Approve'}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-md">
                                    <DialogHeader>
                                        <DialogTitle>
                                            {kycRequest.status === 'rejected'
                                                ? 'Override & Approve KYC Request'
                                                : 'Approve KYC Request'}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        {kycRequest.status === 'rejected' && (
                                            <div className="p-3 bg-warning/10 border border-warning/20 rounded-md">
                                                <p className="text-sm text-warning">
                                                    <strong>Note:</strong> This KYC request was
                                                    previously rejected. You are now overriding that
                                                    decision to approve it.
                                                </p>
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label htmlFor="admin-override">
                                                <input
                                                    type="checkbox"
                                                    id="admin-override"
                                                    checked={isAdminOverride}
                                                    onChange={e =>
                                                        setIsAdminOverride(e.target.checked)
                                                    }
                                                    className="mr-2"
                                                />
                                                Admin Override
                                            </Label>
                                            <p className="text-sm text-muted-foreground">
                                                {kycRequest.status === 'rejected'
                                                    ? 'This will be marked as an admin override since you are changing a rejected status'
                                                    : 'Check this if you are overriding a previous decision'}
                                            </p>
                                        </div>

                                        {(isAdminOverride || kycRequest.status === 'rejected') && (
                                            <div className="space-y-2">
                                                <Label htmlFor="override-reason">
                                                    Override Reason *
                                                </Label>
                                                <Input
                                                    id="override-reason"
                                                    value={overrideReason}
                                                    onChange={e =>
                                                        setOverrideReason(e.target.value)
                                                    }
                                                    placeholder={
                                                        kycRequest.status === 'rejected'
                                                            ? 'Why are you approving this previously rejected request?'
                                                            : 'Why are you overriding this decision?'
                                                    }
                                                />
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label htmlFor="admin-notes">
                                                Admin Notes (Optional)
                                            </Label>
                                            <Textarea
                                                id="admin-notes"
                                                value={adminNotes}
                                                onChange={e => setAdminNotes(e.target.value)}
                                                placeholder="Add any additional notes..."
                                                rows={3}
                                            />
                                        </div>

                                        <div className="flex justify-end gap-2">
                                            <Button
                                                variant="outline"
                                                onClick={() => setIsApproveDialogOpen(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleApprove}
                                                disabled={
                                                    mutation.isPending ||
                                                    (kycRequest.status === 'rejected' &&
                                                        !overrideReason.trim())
                                                }
                                                variant="success"
                                            >
                                                {mutation.isPending ? 'Approving...' : 'Approve'}
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        )}

                        {/* Show reject button for pending and rejected requests */}
                        {(kycRequest.status === 'pending' || kycRequest.status === 'rejected') && (
                            <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button variant="destructive">
                                        <X className="h-4 w-4" />
                                        {kycRequest.status === 'pending'
                                            ? 'Reject'
                                            : 'Override & Reject'}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-md">
                                    <DialogHeader>
                                        <DialogTitle>
                                            {kycRequest.status === 'pending'
                                                ? 'Reject KYC Request'
                                                : 'Override & Reject KYC Request'}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        {kycRequest.status === 'rejected' && (
                                            <div className="p-3 bg-warning/10 border border-warning/20 rounded-md">
                                                <p className="text-sm text-warning">
                                                    <strong>Note:</strong> This KYC request was
                                                    previously rejected. You are now overriding that
                                                    decision to reject it again.
                                                </p>
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label htmlFor="rejection-reason">
                                                Rejection Reason *
                                            </Label>
                                            <Textarea
                                                id="rejection-reason"
                                                value={rejectionReason}
                                                onChange={e => setRejectionReason(e.target.value)}
                                                placeholder="Please provide a reason for rejection..."
                                                rows={3}
                                                required
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="admin-override-reject">
                                                <input
                                                    type="checkbox"
                                                    id="admin-override-reject"
                                                    checked={isAdminOverride}
                                                    onChange={e =>
                                                        setIsAdminOverride(e.target.checked)
                                                    }
                                                    className="mr-2"
                                                />
                                                Admin Override
                                            </Label>
                                            <p className="text-sm text-muted-foreground">
                                                {kycRequest.status === 'rejected'
                                                    ? 'This will be marked as an admin override since you are changing a rejected status'
                                                    : 'Check this if you are overriding a previous decision'}
                                            </p>
                                        </div>

                                        {(isAdminOverride || kycRequest.status === 'rejected') && (
                                            <div className="space-y-2">
                                                <Label htmlFor="override-reason-reject">
                                                    Override Reason
                                                </Label>
                                                <Input
                                                    id="override-reason-reject"
                                                    value={overrideReason}
                                                    onChange={e =>
                                                        setOverrideReason(e.target.value)
                                                    }
                                                    placeholder={
                                                        kycRequest.status === 'rejected'
                                                            ? 'Why are you rejecting this request again?'
                                                            : 'Why are you overriding this decision?'
                                                    }
                                                />
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label htmlFor="admin-notes-reject">
                                                Admin Notes (Optional)
                                            </Label>
                                            <Textarea
                                                id="admin-notes-reject"
                                                value={adminNotes}
                                                onChange={e => setAdminNotes(e.target.value)}
                                                placeholder="Add any additional notes..."
                                                rows={3}
                                            />
                                        </div>

                                        <div className="flex justify-end gap-2">
                                            <Button
                                                variant="outline"
                                                onClick={() => setIsRejectDialogOpen(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleReject}
                                                disabled={
                                                    mutation.isPending || !rejectionReason.trim()
                                                }
                                                variant="destructive"
                                            >
                                                {mutation.isPending ? 'Rejecting...' : 'Reject'}
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        )}

                        {/* Show override reject button for approved requests */}
                        {kycRequest.status === 'approved' && (
                            <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button variant="destructive">
                                        <X className="h-4 w-4" />
                                        Override & Reject
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-md">
                                    <DialogHeader>
                                        <DialogTitle>
                                            Override & Reject Approved KYC Request
                                        </DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                        <div className="p-3 bg-warning/10 border border-warning/20 rounded-md">
                                            <p className="text-sm text-warning">
                                                <strong>Warning:</strong> This KYC request was
                                                previously approved. You are now overriding that
                                                decision to reject it.
                                            </p>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="rejection-reason-approved">
                                                Rejection Reason *
                                            </Label>
                                            <Textarea
                                                id="rejection-reason-approved"
                                                value={rejectionReason}
                                                onChange={e => setRejectionReason(e.target.value)}
                                                placeholder="Please provide a reason for rejection..."
                                                rows={3}
                                                required
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="override-reason-approved">
                                                Override Reason *
                                            </Label>
                                            <Input
                                                id="override-reason-approved"
                                                value={overrideReason}
                                                onChange={e => setOverrideReason(e.target.value)}
                                                placeholder="Why are you rejecting this previously approved request?"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="admin-notes-approved">
                                                Admin Notes (Optional)
                                            </Label>
                                            <Textarea
                                                id="admin-notes-approved"
                                                value={adminNotes}
                                                onChange={e => setAdminNotes(e.target.value)}
                                                placeholder="Add any additional notes..."
                                                rows={3}
                                            />
                                        </div>

                                        <div className="flex justify-end gap-2">
                                            <Button
                                                variant="outline"
                                                onClick={() => setIsRejectDialogOpen(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleReject}
                                                disabled={
                                                    mutation.isPending ||
                                                    !rejectionReason.trim() ||
                                                    !overrideReason.trim()
                                                }
                                                variant="destructive"
                                            >
                                                {mutation.isPending
                                                    ? 'Rejecting...'
                                                    : 'Override & Reject'}
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        )}
                    </>
                }
            />

            {/* Status and Basic Info */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Customer Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Customer Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                        Name
                                    </Label>
                                    <p className="font-medium">{kycRequest.customer.name}</p>
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                        Email
                                    </Label>
                                    <p className="font-medium">{kycRequest.customer.email}</p>
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                        Phone
                                    </Label>
                                    <p className="font-medium">
                                        {kycRequest.customer.phone || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-muted-foreground">
                                        Customer Type
                                    </Label>
                                    <p className="font-medium capitalize">
                                        {kycRequest.customer.type}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* KYC Status */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                KYC Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center gap-4">
                                <StatusBadge status={kycRequest.status} kind="kyc" />
                                <div className="text-sm text-muted-foreground">
                                    Submitted: {format(new Date(kycRequest.submitted_at), 'PPP')}
                                </div>
                            </div>

                            {kycRequest.reviewed_at && (
                                <div className="text-sm text-muted-foreground">
                                    Reviewed: {format(new Date(kycRequest.reviewed_at), 'PPP')}
                                </div>
                            )}

                            {kycRequest.rejection_reason && (
                                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                                    <Label className="text-sm font-medium text-destructive">
                                        Rejection Reason
                                    </Label>
                                    <p className="text-destructive mt-1">
                                        {kycRequest.rejection_reason}
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Documents */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <FileText className="h-5 w-5" />
                                Documents ({kycRequest.documents.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {kycRequest.documents.length === 0 ? (
                                <div className="text-center py-8">
                                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-muted-foreground mb-4">
                                        No documents uploaded
                                    </p>
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsUploadDocumentsModalOpen(true)}
                                        className="flex items-center gap-2"
                                    >
                                        <FileText className="h-4 w-4" />
                                        Upload Documents
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {kycRequest.documents.map(doc => (
                                        <DocumentCard
                                            key={doc.id}
                                            document={doc}
                                            onView={handleViewDocument}
                                            onDownload={handleDownloadDocument}
                                            showDelete={false}
                                        />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Quick Actions</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <Button
                                variant="outline"
                                className="w-full justify-start"
                                onClick={() => navigate(`/customers/${kycRequest.customer.id}`)}
                            >
                                <User className="h-4 w-4" />
                                View Customer Profile
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full justify-start"
                                onClick={() =>
                                    navigate(`/kyc-requests?customer_id=${kycRequest.customer.id}`)
                                }
                            >
                                <FileText className="h-4 w-4" />
                                View All KYC Requests
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Admin Override History */}
                    {(kycRequest.admin_override || kycRequest.admin_notes) && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Shield className="h-5 w-5" />
                                    Admin Actions
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {kycRequest.admin_override && (
                                    <div className="p-2 bg-warning/10 border border-warning/20 rounded-md">
                                        <p className="text-sm text-warning font-medium">
                                            Admin Override
                                        </p>
                                        {kycRequest.override_reason && (
                                            <p className="text-sm text-warning mt-1">
                                                {kycRequest.override_reason}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {kycRequest.admin_notes && (
                                    <div>
                                        <Label className="text-sm font-medium text-muted-foreground">
                                            Admin Notes
                                        </Label>
                                        <p className="text-sm mt-1">{kycRequest.admin_notes}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>

            {/* Upload Documents Modal */}
            <UploadKycDocumentsModal
                isOpen={isUploadDocumentsModalOpen}
                onClose={() => setIsUploadDocumentsModalOpen(false)}
                kycRequestId={id}
                onSuccess={() => {
                    // Refresh KYC request details
                    queryClient.invalidateQueries({ queryKey: ['kycRequestDetails', id] });
                }}
            />
        </PageContainer>
    );
};

export default KycDetailsPage;
